import { useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Plus, Printer, Tags } from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Panel,
  Skeleton,
  Toolbar,
} from '@/ui/Panel';
import { BulkBar, DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { ChipGroup, Pagination } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { SearchInput } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { MultiSelect, parseIdCsv, toIdCsv, useDebouncedValue } from '@/ui/MultiSelect';
import { api, errorMessage } from '@/lib/api/client';
import { useShortcutHint } from '@/app/StatusLine';
import {
  countLabel,
  dayLabel,
  humanize,
  marginPct,
  money,
  num,
  percent,
  qty,
  smartDate,
} from '@/lib/format';
import type {
  BrandSummary,
  DepartmentListResponse,
  ProductDetail,
  ProductListResponse,
  ProductSummary,
  VendorListResponse,
} from '@/lib/api/types';
import { asOptionalText, asText } from '@/lib/searchParams';
import { useFamilies } from '@/features/catalog/families';

/* The catalog list. Selecting a row fills the inspector and writes `?id=`;
   opening the full editor is a deliberate second action (the pencil), because
   most catalog questions are answered by looking, not by editing. */

const PAGE_SIZE = 50;

type ActiveFilter = 'all' | 'true' | 'false';
type StockFilter = 'all' | 'in_stock' | 'out_of_stock';
/** TASK-0259 (FRESH-1): every product, the sold ones, or the input products (bought, never sold). */
export type KindFilter = 'all' | 'sold' | 'inputs';

/** The `GET /products` parameter the kind chip sends; absent = both. */
export function kindToParam(kind: KindFilter): boolean | undefined {
  return kind === 'all' ? undefined : kind === 'inputs';
}

interface ProductSearch {
  id?: string;
  q: string;
  dept: string;
  brand: string;
  vendor: string;
  active: ActiveFilter;
  stock: StockFilter;
  kind: KindFilter;
  /** A CSV of family codes. */
  family: string;
  sort: string;
  order: 'asc' | 'desc';
  page: number;
}

export const Route = createFileRoute('/_app/products')({
  staticData: { crumb: 'Products' },
  // `SearchSchemaInput` separates what a link may pass (anything, or nothing)
  // from what the screen reads (every field, defaulted). Without it every
  // `<Link to="/products">` would have to spell out all nine filters.
  validateSearch: (s: Record<string, unknown> & SearchSchemaInput): ProductSearch => ({
    id: asOptionalText(s.id),
    q: asText(s.q, ''),
    dept: asText(s.dept, ''),
    brand: asText(s.brand, ''),
    vendor: asText(s.vendor, ''),
    active: s.active === 'true' ? 'true' : s.active === 'false' ? 'false' : 'all',
    stock:
      s.stock === 'in_stock'
        ? 'in_stock'
        : s.stock === 'out_of_stock'
          ? 'out_of_stock'
          : 'all',
    kind: s.kind === 'sold' ? 'sold' : s.kind === 'inputs' ? 'inputs' : 'all',
    family: /^[a-z_,]*$/.test(String(s.family ?? '')) ? String(s.family ?? '') : '',
    sort: asText(s.sort, 'name'),
    order: s.order === 'desc' ? 'desc' : 'asc',
    page: Number(s.page) > 0 ? Number(s.page) : 1,
  }),
  component: ProductsScreen,
});

function ProductsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const [rawQuery, setRawQuery] = useState(search.q);
  const debouncedQuery = useDebouncedValue(rawQuery);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'x', label: 'Select' },
    { keys: 'Enter', label: 'Inspect' },
  ]);

  function patch(next: Partial<ProductSearch>) {
    void navigate({ search: (prev: ProductSearch) => ({ ...prev, ...next }) });
  }

  // The typed box drives the URL through a debounce so every keystroke does not
  // land in history or fire a request.
  useEffect(() => {
    if (debouncedQuery !== search.q) {
      void navigate({
        search: (prev: ProductSearch) => ({ ...prev, q: debouncedQuery, page: 1 }),
        replace: true,
      });
    }
    // Deliberately keyed on the debounced value alone: `navigate` and
    // `search.q` both change as a *result* of this effect, so listing them
    // would re-enter it on the navigation it just performed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const departments = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
  });

  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<BrandSummary[]>('/brands'),
    staleTime: 5 * 60_000,
  });

  const vendors = useQuery({
    queryKey: ['vendors', { pageSize: 500 }],
    queryFn: () => api.get<VendorListResponse>('/vendors', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
  });
  const families = useFamilies();

  // Deliberately excludes `id` — selecting a row must not refetch the list.
  const listParams = {
    page: search.page,
    pageSize: PAGE_SIZE,
    search: search.q || undefined,
    department_id: search.dept || undefined,
    brand_id: search.brand || undefined,
    vendor_id: search.vendor || undefined,
    active: search.active === 'all' ? undefined : search.active,
    stock_state: search.stock === 'all' ? undefined : search.stock,
    purchasing_only: kindToParam(search.kind),
    family: search.family || undefined,
    sort: search.sort,
    order: search.order,
  };

  const list = useQuery({
    queryKey: ['products', 'list', listParams],
    queryFn: () => api.get<ProductListResponse>('/products', { params: listParams }),
    placeholderData: (prev) => prev,
  });

  const rows = list.data?.data;
  const total = list.data?.total ?? 0;

  const enqueueLabels = useMutation({
    mutationFn: (productIds: number[]) =>
      api.post<{ enqueued: number }>('/labels/queue', {
        productIds,
        reason: 'manual',
      }),
    onSuccess: (result) => {
      toast.success(`Queued ${countLabel(result.enqueued, 'shelf label')}`, {
        description: 'The label station prints these on its next poll.',
      });
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
    onError: (err) =>
      toast.error('Could not queue labels', { description: errorMessage(err) }),
  });

  const columns: Array<Column<ProductSummary>> = [
    {
      id: 'name',
      header: 'Product',
      sortable: true,
      // Name over brand needs two lines, which is why this table runs at
      // `comfortable` density — at 28px the sublabel spilled into the row above.
      cell: (row) => (
        <div className="min-w-0 leading-tight">
          <div className="flex items-center gap-1.5">
            <span className="text-ink truncate text-sm">{row.name}</span>
            {row.purchasingOnly ? <Badge tone="info">Input</Badge> : null}
          </div>
          {row.brand ? (
            <div className="text-2xs text-ink-faint truncate">{row.brand}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'code',
      header: 'Code',
      width: '5rem',
      mono: true,
      hideBelow: 'xl',
      cell: (row) =>
        row.legacyId ? (
          <span className="text-2xs text-ink-muted">{row.legacyId}</span>
        ) : (
          <span className="text-ink-faint">—</span>
        ),
    },
    {
      id: 'department',
      header: 'Department',
      width: '7rem',
      sortable: true,
      hideBelow: 'md',
      cell: (row) => (
        <div className="min-w-0 leading-tight">
          <div className="text-ink-muted truncate text-xs">{row.department ?? '—'}</div>
          {row.familyName ? (
            <div className="text-2xs text-ink-faint truncate">{row.familyName}</div>
          ) : null}
        </div>
      ),
    },
    {
      id: 'price',
      header: 'Price',
      width: '5rem',
      align: 'right',
      mono: true,
      sortable: true,
      cell: (row) =>
        row.purchasingOnly ? (
          <span className="text-2xs text-ink-faint">not sold</span>
        ) : (
          <span className="text-xs">{money(row.price)}</span>
        ),
    },
    {
      id: 'cost',
      header: 'Cost',
      width: '5rem',
      align: 'right',
      mono: true,
      hideBelow: 'xl',
      cell: (row) => (
        <span className="text-ink-muted text-xs">
          {row.cost == null ? '—' : money(row.cost)}
        </span>
      ),
    },
    {
      id: 'margin',
      header: 'Margin',
      width: '4.5rem',
      align: 'right',
      mono: true,
      sortable: true,
      hideBelow: 'lg',
      cell: (row) => <MarginCell price={row.price} cost={row.cost} />,
    },
    {
      id: 'stock',
      header: 'On hand',
      width: '5rem',
      align: 'right',
      mono: true,
      sortable: true,
      cell: (row) =>
        row.stock == null ? (
          <span className="text-2xs text-ink-faint">untracked</span>
        ) : (
          <span className={row.stock <= 0 ? 'text-negative text-xs' : 'text-xs'}>
            {qty(row.stock)}
          </span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '5rem',
      cell: (row) =>
        row.status === 'active' ? (
          <Badge tone="positive">Active</Badge>
        ) : (
          <Badge tone="warn">{humanize(row.status ?? 'inactive')}</Badge>
        ),
    },
  ];

  const deptOptions = useMemo(
    () =>
      (departments.data?.data ?? []).map((d) => ({
        value: d.id,
        label: d.name,
        hint: d.code,
      })),
    [departments.data]
  );
  const brandOptions = useMemo(
    () =>
      (brands.data ?? []).map((b) => ({
        value: b.id,
        label: b.name,
        hint: b.productCount != null ? num(b.productCount) : undefined,
      })),
    [brands.data]
  );
  const vendorOptions = useMemo(
    () => (vendors.data?.data ?? []).map((v) => ({ value: v.id, label: v.name })),
    [vendors.data]
  );
  const familyOptions = useMemo(
    () =>
      (families.data ?? []).map((f) => ({
        value: f.code,
        label: f.name,
        hint: f.active ? num(f.products) : 'retired',
      })),
    [families.data]
  );

  return (
    <Page>
      <PageHeader
        eyebrow="Catalog"
        title="Products"
        subtitle={list.data ? `${num(total)} matching the current filters` : undefined}
        actions={
          <>
            <Button size="sm" icon={<Tags className="size-3.5" />} asChild>
              <Link to="/products/families">Families…</Link>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Plus className="size-3.5" />}
              asChild
            >
              <Link to="/products/new">New product</Link>
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchInput
          size="sm"
          className="w-64"
          value={rawQuery}
          onValueChange={setRawQuery}
          placeholder="Name or barcode…"
        />
        <MultiSelect
          label="Dept"
          loading={departments.isLoading}
          options={deptOptions}
          selected={parseIdCsv(search.dept)}
          onChange={(next) => patch({ dept: toIdCsv(next), page: 1 })}
        />
        <MultiSelect
          label="Brand"
          loading={brands.isLoading}
          options={brandOptions}
          selected={parseIdCsv(search.brand)}
          onChange={(next) => patch({ brand: toIdCsv(next), page: 1 })}
        />
        <MultiSelect
          label="Vendor"
          loading={vendors.isLoading}
          options={vendorOptions}
          selected={parseIdCsv(search.vendor)}
          onChange={(next) => patch({ vendor: toIdCsv(next), page: 1 })}
        />
        <MultiSelect
          label="Family"
          loading={families.isLoading}
          options={familyOptions}
          selected={search.family ? search.family.split(',').filter(Boolean) : []}
          onChange={(next) => patch({ family: next.map(String).join(','), page: 1 })}
        />
        <div className="bg-line h-4 w-px" />
        <ChipGroup<KindFilter>
          value={search.kind}
          onChange={(v) => patch({ kind: v, page: 1 })}
          options={[
            { value: 'all', label: 'All' },
            { value: 'sold', label: 'Sold' },
            { value: 'inputs', label: 'Inputs' },
          ]}
        />
        <ChipGroup<ActiveFilter>
          value={search.active}
          onChange={(v) => patch({ active: v, page: 1 })}
          options={[
            { value: 'all', label: 'All' },
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
          ]}
        />
        <ChipGroup<StockFilter>
          value={search.stock}
          onChange={(v) => patch({ stock: v, page: 1 })}
          options={[
            { value: 'all', label: 'Any stock' },
            { value: 'in_stock', label: 'In stock' },
            { value: 'out_of_stock', label: 'Out' },
          ]}
        />
      </Toolbar>

      {/* The split view owns its own scrolling, so it sits outside PageBody. */}
      <div className="min-h-0 flex-1">
        <SplitView
          storageKey="products"
          onCloseDetail={() => patch({ id: undefined })}
          placeholder={
            <EmptyState
              compact
              icon={<Package />}
              title="No product selected"
              description="Pick a row to inspect pricing, stock and recent movement without leaving the list."
            />
          }
          detail={search.id ? <ProductInspector id={search.id} /> : undefined}
          list={
            <>
              <DataTable
                className="min-h-0 flex-1"
                density="comfortable"
                rows={rows}
                columns={columns}
                rowKey={(row) => String(row.id)}
                loading={list.isLoading}
                error={list.error}
                onRetry={() => void list.refetch()}
                onRowOpen={(row) => patch({ id: String(row.id) })}
                isRowActive={(row) => String(row.id) === search.id}
                rowTone={(row) =>
                  row.status !== 'active'
                    ? 'warn'
                    : row.stock != null && row.stock <= 0
                      ? 'negative'
                      : null
                }
                selection={{ selected, onChange: setSelected }}
                sort={{
                  by: search.sort,
                  dir: search.order,
                  onChange: (by, dir) => patch({ sort: by, order: dir, page: 1 }),
                }}
                empty={{
                  icon: <Package />,
                  title: 'No products match',
                  description: 'Loosen the filters, or create the product.',
                }}
                footer={
                  <Pagination
                    className="w-full"
                    page={search.page}
                    pageSize={PAGE_SIZE}
                    total={total}
                    onPageChange={(page) => patch({ page })}
                  />
                }
              />
              <BulkBar count={selected.size} onClear={() => setSelected(new Set())}>
                <Button
                  size="xs"
                  icon={<Printer className="size-3" />}
                  loading={enqueueLabels.isPending}
                  onClick={() =>
                    enqueueLabels.mutate(
                      [...selected]
                        .map((v) => Number(v))
                        .filter((n) => Number.isFinite(n))
                    )
                  }
                >
                  Queue shelf labels
                </Button>
              </BulkBar>
            </>
          }
        />
      </div>
    </Page>
  );
}

function MarginCell({ price, cost }: { price: number; cost?: number | null }) {
  if (cost == null) return <span className="text-ink-faint text-xs">—</span>;
  const pct = marginPct(price, cost);
  if (pct === null) return <span className="text-ink-faint text-xs">—</span>;
  return (
    <span
      className={
        pct < 0 ? 'text-negative text-xs' : pct < 10 ? 'text-warn text-xs' : 'text-xs'
      }
    >
      {percent(pct)}
    </span>
  );
}

/* ── Inspector ────────────────────────────────────────────────────────────── */

function ProductInspector({ id }: { id: string }) {
  const detail = useQuery({
    queryKey: ['products', id, { days: 30 }],
    queryFn: () => api.get<ProductDetail>(`/products/${id}`, { params: { days: 30 } }),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-3 p-3">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (detail.error) {
    return (
      <ErrorState
        className="m-3"
        title="Could not load this product"
        message={errorMessage(detail.error)}
        onRetry={() => void detail.refetch()}
      />
    );
  }
  const p = detail.data;
  if (!p) return null;

  const margin = p.cost == null ? null : marginPct(p.price, p.cost);
  const priceHistory = [...(p.priceHistory ?? [])].reverse().slice(0, 6);

  return (
    <div className="flex flex-col gap-3 p-3">
      <div>
        <div className="flex items-start gap-2">
          <h2 className="text-ink min-w-0 flex-1 text-base leading-tight font-semibold">
            {p.name}
          </h2>
          <Button size="xs" icon={<Pencil className="size-3" />} asChild>
            <Link to="/products/$id" params={{ id }}>
              Edit
            </Link>
          </Button>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {p.status === 'active' || p.active ? (
            <Badge tone="positive" dot>
              Active
            </Badge>
          ) : (
            <Badge tone="warn" dot>
              Inactive
            </Badge>
          )}
          {p.brand ? <Badge>{p.brand}</Badge> : null}
          {p.department ? <Badge tone="info">{p.department}</Badge> : null}
          {p.soldByWeight ? <Badge tone="signal">By weight</Badge> : null}
          {p.purchasingOnly ? <Badge tone="info">Input, not sold</Badge> : null}
        </div>
      </div>

      <Panel title="Pricing">
        <DetailList columns={2}>
          <Detail label="Price" mono>
            {p.purchasingOnly ? 'Not sold' : money(p.price)}
          </Detail>
          <Detail label="Cost" mono>
            {p.cost == null ? '—' : money(p.cost)}
          </Detail>
          <Detail label="Margin" mono>
            {margin === null ? '—' : percent(margin)}
          </Detail>
          <Detail label="Avg cost" mono>
            {p.avgCost == null ? '—' : money(p.avgCost)}
          </Detail>
          {p.casePricingEnabled ? (
            <>
              <Detail label="Case qty" mono>
                {p.caseQty == null ? '—' : num(p.caseQty)}
              </Detail>
              <Detail label="Case price" mono>
                {p.casePriceCents == null ? '—' : money(p.casePriceCents)}
              </Detail>
            </>
          ) : null}
        </DetailList>
      </Panel>

      <Panel title="Inventory">
        <DetailList columns={2}>
          <Detail label="On hand" mono>
            {p.stock == null ? 'Untracked' : qty(p.stock)}
          </Detail>
          <Detail label="Reserved" mono>
            {qty(p.reservedQty ?? 0)}
          </Detail>
          <Detail label="Reorder point" mono>
            {p.reorderPoint == null ? '—' : num(p.reorderPoint)}
          </Detail>
          <Detail label="Tracking">
            {p.purchasingOnly
              ? 'Not tracked (input product)'
              : humanize(p.inventoryTracking ?? 'unclassified')}
          </Detail>
          <Detail label="Family">{p.familyName ?? '—'}</Detail>
        </DetailList>
      </Panel>

      {p.purchasingOnly ? (
        <Panel title="Usually becomes">
          {(p.becomes ?? []).length === 0 ? (
            <p className="text-2xs text-ink-faint">Nothing linked yet.</p>
          ) : (
            <ul className="space-y-1">
              {(p.becomes ?? []).map((b) => (
                <li key={b.productId} className="flex items-baseline gap-2 text-xs">
                  <Link
                    to="/products/$id"
                    params={{ id: String(b.productId) }}
                    className="text-ink min-w-0 flex-1 truncate hover:underline"
                  >
                    {b.name}
                  </Link>
                  <span className="text-2xs text-ink-faint">
                    {b.soldLastOn
                      ? `last sold ${dayLabel(b.soldLastOn)}`
                      : 'not sold yet'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      ) : (p.inputs ?? []).length > 0 ? (
        <Panel title="Inputs">
          <ul className="space-y-1">
            {(p.inputs ?? []).map((i) => (
              <li
                key={`${i.productId}-${i.vendorId ?? 'none'}`}
                className="flex items-baseline gap-2 text-xs"
              >
                <Link
                  to="/products/$id"
                  params={{ id: String(i.productId) }}
                  className="text-ink min-w-0 flex-1 truncate hover:underline"
                >
                  {i.name}
                </Link>
                <span className="text-2xs text-ink-faint">
                  {i.lastPaidUnitCostCents != null
                    ? `last paid ${money(i.lastPaidUnitCostCents)} / ${i.unit}`
                    : 'not bought yet'}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel title="Location">
        <DetailList columns={2}>
          <Detail label="Zone">{p.zoneName ?? '—'}</Detail>
          <Detail label="Shelf">{p.shelfLocation ?? '—'}</Detail>
          <Detail label="Pick order" mono>
            {p.pickSequence == null ? '—' : num(p.pickSequence)}
          </Detail>
          <Detail label="Storage">{humanize(p.storageType ?? 'shelf')}</Detail>
        </DetailList>
      </Panel>

      <Panel title="Flags">
        <div className="flex flex-wrap gap-1.5">
          <FlagChip on={!!p.taxable} label="Taxable" />
          <FlagChip on={!!p.ebtEligible} label="EBT" />
          <FlagChip on={!!p.wicEligible} label="WIC" />
          <FlagChip on={!!p.soldByWeight} label="Sold by weight" />
          <FlagChip on={!!p.isPreparedFood} label="Prepared food" />
          <FlagChip on={!!p.expiryRequired} label="Expiry tracked" />
        </div>
      </Panel>

      <Panel title="Barcodes" description={`${(p.barcodes ?? []).length} on file`}>
        {(p.barcodes ?? []).length === 0 ? (
          <p className="text-2xs text-ink-faint">
            No barcodes — this item can only be rung by PLU search.
          </p>
        ) : (
          <ul className="space-y-1">
            {(p.barcodes ?? []).map((b) => (
              <li key={String(b.id)} className="flex items-center gap-2">
                <Code copyable>{b.code}</Code>
                <span className="text-2xs text-ink-faint tracking-wider uppercase">
                  {b.type ?? 'upc'}
                </span>
                {b.isPrimary ? <Badge tone="signal">Primary</Badge> : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Vendor sourcing">
        {(p.vendors ?? []).length === 0 ? (
          <p className="text-2xs text-ink-faint">No vendor linked.</p>
        ) : (
          <ul className="space-y-1.5">
            {(p.vendors ?? []).map((v) => (
              <li key={`${v.vendorId}`} className="flex items-baseline gap-2 text-xs">
                <span className="text-ink min-w-0 flex-1 truncate">
                  {v.vendorName ?? `Vendor ${v.vendorId}`}
                </span>
                {v.isPreferred ? <Badge tone="positive">Preferred</Badge> : null}
                {v.vendorSku ? <Code>{v.vendorSku}</Code> : null}
                <span className="text-ink-muted font-mono tabular-nums">
                  {v.unitCost == null ? '—' : money(v.unitCost)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Recent price changes">
        {priceHistory.length === 0 ? (
          <p className="text-2xs text-ink-faint">No price change on record.</p>
        ) : (
          <ul className="space-y-1">
            {priceHistory.map((h) => (
              <li key={h.id} className="flex items-baseline gap-2 text-xs">
                <span className="text-2xs text-ink-faint w-20 shrink-0 font-mono tabular-nums">
                  {smartDate(h.changedAt)}
                </span>
                <span className="text-ink-muted font-mono tabular-nums">
                  {h.oldPrice == null ? '—' : money(h.oldPrice)}
                </span>
                <span className="text-ink-faint">→</span>
                <span className="text-ink font-mono tabular-nums">
                  {money(h.newPrice)}
                </span>
                <span className="text-2xs text-ink-faint ml-auto">
                  {humanize(h.source ?? '')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Recent inventory movement">
        {(p.recentAdjustments ?? []).length === 0 ? (
          <p className="text-2xs text-ink-faint">No adjustments recorded.</p>
        ) : (
          <ul className="space-y-1">
            {(p.recentAdjustments ?? []).map((a) => {
              const delta = Number(a.quantityDelta ?? 0);
              return (
                <li key={a.id} className="flex items-baseline gap-2 text-xs">
                  <span className="text-2xs text-ink-faint w-20 shrink-0 font-mono tabular-nums">
                    {smartDate(a.occurredAt)}
                  </span>
                  <span className="text-ink-muted truncate">
                    {humanize(a.adjustmentType)}
                  </span>
                  <span
                    className={`ml-auto font-mono tabular-nums ${
                      delta < 0 ? 'text-negative' : 'text-positive'
                    }`}
                  >
                    {delta > 0 ? '+' : ''}
                    {qty(delta)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <Panel title="Last 30 days">
        <DetailList columns={2}>
          <Detail label="Units sold" mono>
            {qty(p.salesSummary?.units ?? 0)}
          </Detail>
          <Detail label="Revenue" mono>
            {money(p.salesSummary?.revenue ?? 0)}
          </Detail>
          <Detail label="Avg price" mono>
            {money(p.salesSummary?.avgPrice ?? 0)}
          </Detail>
          <Detail label="Transactions" mono>
            {num(p.salesSummary?.transactions ?? 0)}
          </Detail>
        </DetailList>
      </Panel>
    </div>
  );
}

function FlagChip({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={
        on
          ? 'bg-positive-muted text-2xs text-positive rounded-xs px-1.5 py-0.5 font-medium'
          : 'bg-neutral-chip text-2xs text-ink-faint rounded-xs px-1.5 py-0.5 line-through'
      }
    >
      {label}
    </span>
  );
}
